import React from 'react';
import { CheckCircle, Package, Users, Zap } from 'lucide-react';
import { SubscriptionTier } from '../lib/supabase';

interface SubscriptionPlanCardProps {
  tier: SubscriptionTier;
  isActive?: boolean;
  onSubscribe: () => void;
}

export function SubscriptionPlanCard({ tier, isActive = false, onSubscribe }: SubscriptionPlanCardProps) {
  const getIconForFeature = (feature: string) => {
    if (feature.toLowerCase().includes('agent')) return Package;
    if (feature.toLowerCase().includes('support')) return Users;
    return Zap;
  };

  return (
    <div className={`bg-white rounded-xl overflow-hidden transition-all duration-200 ${
      isActive 
        ? 'border-2 border-green-500 shadow-lg transform scale-105' 
        : 'border border-gray-200 hover:border-green-300 hover:shadow-md'
    }`}>
      {/* Header */}
      <div className={`p-6 ${isActive ? 'bg-green-50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
          {isActive && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Current Plan
            </span>
          )}
        </div>
        <div className="flex items-baseline">
          <span className="text-3xl font-bold text-gray-900">${tier.price}</span>
          <span className="text-gray-600 ml-1">/{tier.interval}</span>
        </div>
        <p className="text-sm text-gray-600 mt-2">{tier.description}</p>
      </div>

      {/* Features */}
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">Up to {tier.agent_limit} premium agents</span>
          </div>
          <div className="space-y-2">
            {tier.features.map((feature, index) => {
              const Icon = getIconForFeature(feature);
              return (
                <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={onSubscribe}
          className={`w-full py-2 rounded-lg transition-colors ${
            isActive
              ? 'bg-gray-200 text-gray-700 cursor-default'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
          disabled={isActive}
        >
          {isActive ? 'Current Plan' : 'Subscribe'}
        </button>
      </div>
    </div>
  );
}